import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';

const AuthDiagnosticPage: React.FC = () => {
  const navigate = useNavigate();
  const [diagnostics, setDiagnostics] = useState<any>({});

  useEffect(() => {
    const token = localStorage.getItem('token');
    const tokenExpiresAt = localStorage.getItem('tokenExpiresAt');
    const hotelId = localStorage.getItem('hotelId');
    const hotelName = localStorage.getItem('hotelName');
    const user = localStorage.getItem('user');

    let tokenPayload = null;
    let tokenExpired = false;
    let tokenValid = false;

    if (token) {
      try {
        const parts = token.split('.');
        if (parts.length === 3) {
          tokenPayload = JSON.parse(atob(parts[1]));
          tokenValid = true;
          
          // Check expiration from token payload
          if (tokenPayload.exp) {
            const expDate = new Date(tokenPayload.exp * 1000);
            tokenExpired = expDate < new Date();
          }
        }
      } catch (e) {
        console.error('Failed to parse token:', e);
      }
    }

    setDiagnostics({
      hasToken: !!token,
      tokenPreview: token ? token.substring(0, 50) + '...' : null,
      tokenExpiresAt,
      tokenExpired,
      tokenValid,
      tokenPayload,
      hotelId,
      hotelName,
      user: (() => { try { return user ? JSON.parse(user) : null; } catch { return null; } })(),
      isAuthenticated: authService.isAuthenticated(),
    });
  }, []);

  const handleRelogin = () => {
    authService.logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Authentication Diagnostics</h1>

        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          <div>
            <h2 className="text-lg font-semibold mb-2">Authentication Status</h2>
            <div className={`inline-block px-3 py-1 rounded ${
              diagnostics.isAuthenticated ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {diagnostics.isAuthenticated ? 'Authenticated' : 'Not Authenticated'}
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">Token Information</h2>
            <dl className="space-y-2">
              <div>
                <dt className="font-medium text-gray-700">Has Token:</dt>
                <dd className={diagnostics.hasToken ? 'text-green-600' : 'text-red-600'}>
                  {diagnostics.hasToken ? 'Yes' : 'No'}
                </dd>
              </div>
              {diagnostics.hasToken && (
                <>
                  <div>
                    <dt className="font-medium text-gray-700">Token Valid:</dt>
                    <dd className={diagnostics.tokenValid ? 'text-green-600' : 'text-red-600'}>
                      {diagnostics.tokenValid ? 'Yes' : 'No (malformed)'}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-700">Token Expired:</dt>
                    <dd className={diagnostics.tokenExpired ? 'text-red-600' : 'text-green-600'}>
                      {diagnostics.tokenExpired ? 'Yes' : 'No'}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-700">Token Expires At:</dt>
                    <dd className="text-gray-900">{diagnostics.tokenExpiresAt || 'Not set'}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-700">Token Preview:</dt>
                    <dd className="text-gray-900 font-mono text-xs break-all">
                      {diagnostics.tokenPreview}
                    </dd>
                  </div>
                </>
              )}
            </dl>
          </div>

          {diagnostics.tokenPayload && (
            <div>
              <h2 className="text-lg font-semibold mb-2">Token Payload</h2>
              <pre className="bg-gray-100 p-4 rounded text-xs overflow-auto">
                {JSON.stringify(diagnostics.tokenPayload, null, 2)}
              </pre>
            </div>
          )}

          <div>
            <h2 className="text-lg font-semibold mb-2">Hotel Information</h2>
            <dl className="space-y-2">
              <div>
                <dt className="font-medium text-gray-700">Hotel ID:</dt>
                <dd className="text-gray-900">{diagnostics.hotelId || 'Not set'}</dd>
              </div>
              <div>
                <dt className="font-medium text-gray-700">Hotel Name:</dt>
                <dd className="text-gray-900">{diagnostics.hotelName || 'Not set'}</dd>
              </div>
            </dl>
          </div>

          {diagnostics.user && (
            <div>
              <h2 className="text-lg font-semibold mb-2">User Information</h2>
              <pre className="bg-gray-100 p-4 rounded text-xs overflow-auto">
                {JSON.stringify(diagnostics.user, null, 2)}
              </pre>
            </div>
          )}

          <div className="pt-4 border-t">
            <h2 className="text-lg font-semibold mb-4">Actions</h2>
            <div className="space-x-3">
              <button
                onClick={handleRelogin}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Logout and Re-login
              </button>
              <button
                onClick={() => navigate('/checkin')}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Try Check-in Again
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthDiagnosticPage;
